import type { AccessRole } from './identity.constant';

/**
 * ─── [C5c §11] A UNIT MAY ACT ONE OR TWO LEVELS UP ─────────────────────────────────────────
 *
 * A setting on the ORG UNIT, not on a person: whichever functional roles sit at an elevated unit
 * act at the ancestor's level — admin as admin, monitor as monitor, read as read. The posting does
 * not move (11a); only the REACH does.
 *
 * ⚠️ DERIVED, NEVER STORED ON AN ORDER OR AN OFFICER (11i). The acting unit is resolved from the
 * posting unit's own `ancestors` and `elevation` in ONE place per request (`getAuthContext`), and
 * the `Scope` built from it roots at the acting unit — so every scope read in the system follows
 * with no edit, and nothing is added to `visible_org_unit_ids`. See the CHANGELOG for the count.
 *
 * ⚠️ IT DOES NOT COMPOSE (11e). Only the officer's OWN posting unit's setting is read, against the
 * STORED tree: a DRO elevated two levels reaches the commissionerate whether or not its division
 * is elevated, and a division elevated one level does not lift the DROs beneath it.
 *
 * ⚠️ AN UNKNOWN VALUE READS AS `NONE`, which is the closed direction — the column carries no model
 * enum because `org_units` rows are hand-edited on Atlas, `officers.flags`' reason: one mongosh
 * typo would otherwise make every later save of that unit throw.
 */
export const ORG_UNIT_ELEVATIONS = ['NONE', 'ONE_UP', 'TWO_UP'] as const;
export type OrgUnitElevation = (typeof ORG_UNIT_ELEVATIONS)[number];

const LEVELS_UP: Record<OrgUnitElevation, number> = { NONE: 0, ONE_UP: 1, TWO_UP: 2 };

export const asOrgUnitElevation = (value: unknown): OrgUnitElevation =>
  (ORG_UNIT_ELEVATIONS as readonly unknown[]).includes(value) ? (value as OrgUnitElevation) : 'NONE';

/**
 * The unit an officer posted at `unit` ACTS at.
 *
 * `ancestors` is root-first and EXCLUDES self, so the parent is the last entry and two up is the
 * one before it. Two up from a unit whose parent IS the root clamps at the root: "two levels up
 * from a division reaches the commissionerate" (11b) is the case the brief names, and a division
 * sits one below it.
 */
export const actingOrgUnitIdOf = <T>(unit: {
  readonly _id: T;
  readonly ancestors: readonly T[];
  readonly elevation?: unknown;
}): T => {
  const levels = LEVELS_UP[asOrgUnitElevation(unit.elevation)];
  if (levels === 0 || unit.ancestors.length === 0) return unit._id;
  return unit.ancestors[Math.max(0, unit.ancestors.length - levels)] ?? unit._id;
};

/**
 * Which roles an elevation lifts: the three nodal roles. A super admin is GLOBAL already, and an
 * officer with no role holds no `Scope` at all — so neither is ever "acting" anywhere.
 */
export const elevationAppliesTo = (role: AccessRole): boolean => role !== 'SUPER_ADMIN' && role !== 'NONE';

/**
 * [C5c §11c] The three refusals an elevated reach adds to officer and org-unit writes — officers
 * posted AT the acting level, the acting NODE itself, and a posting that acts above your own level.
 * One sentence each, naming the STATE rather than the person, so the web and the API read one literal.
 */
export const ELEVATED_OVER_ANCESTOR_MESSAGE =
  'Your unit acts at this level from below it, so officers posted at this level itself can be viewed but not changed.';
export const ELEVATED_ACTING_NODE_MESSAGE =
  'Your unit acts at this level from below it, so this unit itself can be viewed but not changed. Ask a super admin.';
export const ELEVATED_TARGET_ABOVE_MESSAGE =
  'This posting acts above your own level, so it cannot be changed from here. Ask a super admin.';
